import React, { useState, useEffect } from 'react';
import axios from 'axios';

interface AppVersionSettingsState {
  id?: number;
  latest_version: string;
  force_update_required: boolean;
  update_message?: string;
  android_package_name: string;
  ios_app_id: string;
  created_at?: string;
  updated_at?: string;
}

const AppVersionSettings: React.FC = () => {
  const [settings, setSettings] = useState<AppVersionSettingsState>({
    latest_version: '1.0.0',
    force_update_required: false,
    update_message: '',
    android_package_name: 'com.nyvro.app',
    ios_app_id: '',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get('/admin/app-version-settings');
      if (response.data.success && response.data.data) {
        setSettings({
          latest_version: response.data.data.latest_version || '1.0.0',
          force_update_required: response.data.data.force_update_required || false,
          update_message: response.data.data.update_message || '',
          android_package_name: response.data.data.android_package_name || 'com.nyvro.app',
          ios_app_id: response.data.data.ios_app_id || '',
        });
      }
    } catch (error: any) {
      console.error('Error loading app version settings:', error);
      setError(error.response?.data?.message || 'Failed to load app version settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess(null);

    // Validate version format (semantic versioning: major.minor.patch)
    const versionRegex = /^\d+\.\d+\.\d+$/;
    if (!versionRegex.test(settings.latest_version)) {
      setError('Version must be in semantic versioning format (e.g., 1.0.0)');
      setSaving(false);
      return;
    }

    try {
      const payload = {
        latest_version: settings.latest_version,
        force_update_required: settings.force_update_required,
        update_message: settings.update_message?.trim() || null,
        android_package_name: settings.android_package_name,
        ios_app_id: settings.ios_app_id,
      };

      const response = await axios.put('/admin/app-version-settings', payload);
      if (response.data.success) {
        setSuccess('App version settings updated successfully');
        setSettings({
          ...settings,
          updated_at: response.data.data.updated_at,
        });
        // Clear success message after 5 seconds
        setTimeout(() => setSuccess(null), 5000);
      }
    } catch (error: any) {
      console.error('Error updating app version settings:', error);
      setError(error.response?.data?.message || 'Failed to update app version settings');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field: keyof AppVersionSettingsState, value: any) => {
    setSettings({
      ...settings,
      [field]: value,
    });
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="page-content">
      <div className="container-fluid">
        <div className="row">
          <div className="col-12">
            <div className="page-title-box d-sm-flex align-items-center justify-content-between">
              <h4 className="mb-sm-0">App Version Settings</h4>
              <div className="page-title-right">
                <ol className="breadcrumb m-0">
                  <li className="breadcrumb-item">
                    <a href="/dashboard">Dashboard</a>
                  </li>
                  <li className="breadcrumb-item">
                    <a href="/system">System</a>
                  </li>
                  <li className="breadcrumb-item active">App Version Settings</li>
                </ol>
              </div>
            </div>
          </div>
        </div>

        <div className="row">
          <div className="col-lg-8">
            <div className="card">
              <div className="card-header">
                <h5 className="card-title mb-0">Manage App Version & Force Update</h5>
              </div>
              <div className="card-body">
                {error && (
                  <div className="alert alert-danger alert-dismissible fade show" role="alert">
                    {error}
                    <button
                      type="button"
                      className="btn-close"
                      onClick={() => setError(null)}
                    ></button>
                  </div>
                )}

                {success && (
                  <div className="alert alert-success alert-dismissible fade show" role="alert">
                    {success}
                    <button
                      type="button"
                      className="btn-close"
                      onClick={() => setSuccess(null)}
                    ></button>
                  </div>
                )}

                <form onSubmit={handleSubmit}>
                  <div className="mb-3">
                    <label htmlFor="latest_version" className="form-label">
                      Latest App Version <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      id="latest_version"
                      value={settings.latest_version}
                      onChange={(e) => handleChange('latest_version', e.target.value)}
                      placeholder="1.0.0"
                      required
                      pattern="^\d+\.\d+\.\d+$"
                    />
                    <div className="form-text">
                      Semantic versioning format: major.minor.patch (e.g., 1.0.0)
                    </div>
                  </div>

                  <div className="mb-3">
                    <div className="form-check form-switch">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="force_update_required"
                        checked={settings.force_update_required}
                        onChange={(e) => handleChange('force_update_required', e.target.checked)}
                      />
                      <label className="form-check-label" htmlFor="force_update_required">
                        Force Update Required
                      </label>
                    </div>
                    <div className="form-text">
                      If enabled, users will be forced to update before using the app
                    </div>
                  </div>

                  <div className="mb-3">
                    <label htmlFor="update_message" className="form-label">
                      Update Message (Optional)
                    </label>
                    <textarea
                      className="form-control"
                      id="update_message"
                      rows={4}
                      value={settings.update_message || ''}
                      onChange={(e) => handleChange('update_message', e.target.value)}
                      placeholder="Leave empty to use default message: 'A new version of the app is available. Please update to continue using Nyvro.'"
                    />
                    <div className="form-text">
                      Custom message to display on the update screen. If empty, a default message will be shown.
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label htmlFor="android_package_name" className="form-label">
                        Android Package Name <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        id="android_package_name"
                        value={settings.android_package_name}
                        onChange={(e) => handleChange('android_package_name', e.target.value)}
                        placeholder="com.nyvro.app"
                        required
                      />
                      <div className="form-text">
                        Used for Play Store redirect
                      </div>
                    </div>

                    <div className="col-md-6 mb-3">
                      <label htmlFor="ios_app_id" className="form-label">
                        iOS App ID
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        id="ios_app_id"
                        value={settings.ios_app_id}
                        onChange={(e) => handleChange('ios_app_id', e.target.value)}
                        placeholder="1234567890"
                      />
                      <div className="form-text">
                        Used for App Store redirect
                      </div>
                    </div>
                  </div>

                  <div className="d-flex justify-content-end gap-2">
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={loadSettings}
                      disabled={saving}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={saving}
                    >
                      {saving ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                          Saving...
                        </>
                      ) : (
                        'Save Settings'
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>

          <div className="col-lg-4">
            <div className="card">
              <div className="card-header">
                <h5 className="card-title mb-0">Information</h5>
              </div>
              <div className="card-body">
                <div className="alert alert-info">
                  <h6 className="alert-heading">
                    <i className="bx bx-info-circle me-2"></i>
                    How It Works
                  </h6>
                  <p className="mb-2">
                    When users open the app, it will check their current version against the latest version configured here.
                  </p>
                  <ul className="mb-0 small">
                    <li>If version is outdated, users see the update screen</li>
                    <li>If "Force Update" is enabled, users cannot skip the update</li>
                    <li>Clicking "Update Now" redirects to Play Store / App Store</li>
                  </ul>
                </div>

                {settings.updated_at && (
                  <div className="mt-3">
                    <small className="text-muted">
                      Last updated: {new Date(settings.updated_at).toLocaleString()}
                    </small>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AppVersionSettings;

